import SimpleCollectionList  from "../Shared/Collections/SimpleCollectionList";

export {WindowDragControllerSingleton as default};

/**
* Drag controller
*
* Only Handles Window js drag events and does not
* handle element drag
*
* Use this controller if you need to take control of the window drag
* and block window drag events from executing
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class WindowDragController {
  /**
   * Defines the number of drag events fired
   *
   * This is needed because js fires a drag event
   * everytime we enter a child element
   *
   * @private
   *
   * @param {Number}
   *
   * @since 7.0.0
   */
  counter = 0;

  /**
   * Holds all registered drag events
   *
   * @private
   *
   * @param {SimpleCollectionList}
   *
   * @since 7.0.0
   */
  drags = new SimpleCollectionList();

  /**
   * Defines current active drag
   *
   * @param {String}
   *
   * @private
   *
   * @since 7.0.0
   */
  active;

  /**
   * Defines if window drag is enabled
   *
   * @param {Boolean}
   *
   * @private
   *
   * @since 7.0.0
   */
  enabled = true;

    /**
   * Disables window drag events
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
    disable() {
      this.enabled = false;
    }
    /**
     * Enables window drag events
     *
     * @public
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    enable() {
      this.enabled = true;
    }

    /**
     * Registers a drag event
     *
     * Stops broadcasting previous registered drag events
     *
     * @public
     *
     * @param {String} id        drag event id
     * @param {Object} callbacks drag event callbacks
     *
     * @since 7.0.0
     *
     * @return {void}
     */
    register(id, callbacks) {
      this.drags.add(callbacks, id);
      this.active = id;

      if (this.drags.count === 1) {
        this.setListeners();
      }
    }

    /**
     * Destroys the drag event
     *
     * The previous registered event will resume broadcasting
     *
     * @public
     *
     * @param {String} id drag event id
     *
     * @return {void}
     */
    unregister(id) {
      this.drags.delete(id);
      this.drag_active = this.drags.items[this.drags.count - 1] || undefined;

      if (this.drags.count === 0) {
        this.removeListeners();
      }
    }

  /**
   * Registers window drag event listeners
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setListeners() {
    window.addEventListener('drop'     , this.onWindowDrop);
    window.addEventListener('dragenter', this.onWindowDragEnter);
    window.addEventListener('dragleave', this.onWindowDragLeave);
    window.addEventListener('dragover' , this.onWindowDragOver);
  }

  /**
   * Removes window drag event listeners
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  removeListeners() {
    window.removeEventListener('drop'     , this.onWindowDrop);
    window.removeEventListener('dragenter', this.onWindowDragEnter);
    window.removeEventListener('dragleave', this.onWindowDragLeave);
    window.removeEventListener('dragover' , this.onWindowDragOver);
  }

  /**
   * Executes on window drag enter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  onWindowDragEnter(event) {
    if (WindowDragControllerSingleton.enabled === false) {
      return;
    }

    if (WindowDragControllerSingleton.counter === 0) {
      WindowDragControllerSingleton.callback('windowDragEnter', event);
    }

    event.preventDefault();
    event.stopPropagation();
    WindowDragControllerSingleton.increaseCounter();
  }

  /**
   * Executes on window drag leave
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  onWindowDragLeave(event) {
    if (WindowDragControllerSingleton.enabled === false) {
      return;
    }

    if (WindowDragControllerSingleton.counter === 1) {
      WindowDragControllerSingleton.callback('windowDragLeave', event);
    }

    event.preventDefault();
    event.stopPropagation();
    WindowDragControllerSingleton.decreaseseCounter();
  }

  /**
   * Executes on window drag over
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  onWindowDragOver(event) {
    if (WindowDragControllerSingleton.enabled === false) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Executes on window drag drop
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  onWindowDrop(event) {
    if (WindowDragControllerSingleton.enabled === false) {
      return;
    }

    WindowDragControllerSingleton.resetCounter();
    WindowDragControllerSingleton.callback('windowDrop', event);

    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Increases drag event counter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  increaseCounter() {
    this.counter++;
  }

  /**
   * Decreases drag event counter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  decreaseseCounter() {
    this.counter--;
  }

  /**
   * Resets drag event counter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  resetCounter() {
    this.counter = 0;
  }

  /**
   * Executes drag event callback
   *
   * @private
   *
   * @param {String}    type  drag event type
   * @param {DragEvent} event drag event
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  callback(type, event) {
    let callback = this.drags.get(this.active);

    if (!callback[type]) {
      return
    }

    callback[type](event);
  }
}

const WindowDragControllerSingleton = new WindowDragController();